import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, TextField, Typography } from '@glplus/glpweb-dsl';
import { Panel } from './Panel';
import { runnerApi, RunnerApiError, type Me } from '../lib/runnerApiClient';
import { formatUsPhone, toUsE164 } from '../lib/phone';
import { RunnerAction } from './RunnerAction';

/**
 * Rod, 2026-10-01: every Runner gives a phone — Stripe will not issue the store
 * card without one. The application self-edit closes at the Checkr handoff, so
 * this is how an approved Runner adds or changes theirs.
 */
export function PhonePanel({ me, onSaved }: { me: Me; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(formatUsPhone(me.phoneE164));
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  async function save() {
    const e164 = toUsE164(phone);
    if (!e164) return setFlash({ kind: 'error', msg: t('phone.invalid') });
    setBusy(true);
    setFlash(null);
    try {
      await runnerApi.setPhone(e164);
      setPhone(formatUsPhone(e164));
      setFlash({ kind: 'success', msg: t('phone.saved') });
      await onSaved();
    } catch (e) {
      setFlash({ kind: 'error', msg: e instanceof RunnerApiError ? e.message : t('phone.saveFailed') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={t('phone.title')} dataTestId="account-phone">
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('phone.note')}</Typography>
      {me.phoneRequired && !flash && (
        <Box sx={{ mb: 1.5 }}>
          <Alert severity="warning" data-testid="account-phone-missing">{t('phone.missing')}</Alert>
        </Box>
      )}
      <TextField label={t('phone.title')} ariaLabel={t('phone.title')} dataTestId="account-phone-input" size="small" fullWidth value={phone} disabled={busy || !(me.capabilities.includes('runner:profile:write') || me.capabilities.includes('runner:onboarding:write'))} onChange={(e) => setPhone(e.target.value)} placeholder="(704) 555-0100" />
      {flash && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity={flash.kind} data-testid={`account-phone-${flash.kind}`}>{flash.msg}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <RunnerAction topic="account" appearance="primary" ariaLabel={t('phone.save')} dataTestId="account-phone-save" buttonText={t('phone.save')} size="small" disabled={busy || !(me.capabilities.includes('runner:profile:write') || me.capabilities.includes('runner:onboarding:write'))} onClick={() => void save()} />
      </Box>
    </Panel>
  );
}
